/**
 * MaintenanceCompletionReport - Full-screen read-only maintenance completion detail view
 *
 * MCA Compliance: ISM Code Element 9, WBC3 SMS requirements
 * Shows full completion record details with audit history for inspections
 *
 * Follows exact pattern from DrillDetailView.js for design consistency
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Calendar,
  Clock,
  User,
  CheckCircle,
  XCircle,
  FileText,
  AlertTriangle,
  History,
  Edit3,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Wrench,
  Settings,
  Link2,
  Camera,
  ShieldCheck,
  Lock,
  Loader2,
  X
} from 'lucide-react';
import { useSwipeBack, SwipeIndicator } from '../../../../hooks/useSwipeBack';
// FT-357: shell-level back handler — bottom bar owns Back. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
// FT-403: mobile bottom bar action slot — sign-off button lives here on mobile.
import { useSetBottomBarActions } from '../../../../contexts/MobileBottomBarActionsContext';
// FT-402: RBAC gate for the sign-off action (mirrors DrillDetailView).
import { usePermissions } from '../../../../hooks/usePermissions';
// FT-402: "Name (Role)" formatter for the signed-off banner (mirrors drills/defects).
import { formatUserIdentity } from '../../../../utils/userDisplay';
import { Badge } from '../../../catalyst/badge';
import EditReasonModal from '../../../common/EditReasonModal';
// FT-349e: reuse PhotoAttachment in disabled mode for the read-only viewer
import PhotoAttachment from '../../../common/PhotoAttachment';
// FT-221: Removed resolveCrewName - name now stored directly in record.performerName
// FT-267 §16: surface the resolved-canonical category as a human label on the
// completion detail view. Falls back to the raw stored value for free-text
// custom categories.
import { getCategoryLabel } from '../../../../config/maintenanceRequirements-wrapper';

const MaintenanceCompletionReport = ({
  completion,        // The completion record
  job,               // The parent maintenance job (optional)
  equipment = [],    // Array of linked equipment (optional)
  currentVesselId,   // FT-349e: needed by PhotoAttachment to load photos from IDB
  // FT-221: crew prop removed - name now read directly from record.performerName
  onBack,            // Navigate back function
  onEdit,            // Edit completion function (future)
  // FT-209: Navigation callback for defect cross-link
  onNavigateToDefect,
  // FT-402: sign-off callback — parent wires this to
  // useMaintenanceHistory.signOffCompletion(completion.id). Absent for callers
  // that don't support sign-off (button simply not rendered).
  onSignOff
}) => {
  // FT-357: register onBack with the shell-level MobileBottomBar.
  useSetBackHandler(onBack);

  const { containerRef, swipeProgress } = useSwipeBack(onBack);
  const [showEditModal, setShowEditModal] = useState(false);
  const [auditExpanded, setAuditExpanded] = useState(true);
  // FT-402: local pending flag for the sign-off action (mirrors DrillDetailView).
  const [isSigningOff, setIsSigningOff] = useState(false);
  // FT-403: confirmation dialog before sign-off fires.
  const [showSignOffConfirm, setShowSignOffConfirm] = useState(false);

  // FT-402: RBAC gate — only skipper+ (with vessel assignment) may sign off.
  // Field difference vs drills: maintenance uses signedOffStatus/'signed_off'
  // (drills uses reviewStatus/'approved').
  const { can } = usePermissions();
  const canSignOff = can('sign', 'maintenance');
  const isSignedOff = completion?.signedOffStatus === 'signed_off';
  // Pending = record created but not yet signed off (default for all new completions).
  const isAwaitingSignOff = completion?.signedOffStatus === 'pending' && !isSignedOff;
  // FT-349e: ref used by scroll-into-view when opened from a row with photos
  const photosRef = useRef(null);

  // FT-349e: if the caller flagged scrollToPhotos and this record has photos,
  // scroll the photos section into view once mounted.
  useEffect(() => {
    if (completion?.scrollToPhotos && photosRef.current) {
      photosRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [completion?.scrollToPhotos]);

  // FT-221: Use performerName if present (saved at write time), else 'Not recorded'
  // Do NOT use resolveCrewName() - it may fail if crew list not loaded
  const getCrewDisplayName = (crewId, record = null) => {
    // FT-221: Check for pre-resolved performerName first (new records)
    if (record?.performerName) {
      return record.performerName;
    }
    // Legacy fallback: show 'Not recorded' (no resolver, no UUID display)
    return 'Not recorded';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Format timestamp for audit
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle edit button click
  const handleEditClick = () => {
    setShowEditModal(true);
  };

  // Handle edit confirmation with reason
  const handleEditConfirm = (reason) => {
    setShowEditModal(false);
    if (onEdit) {
      onEdit(completion, reason);
    }
  };

  // FT-403: open the confirmation dialog (called by both header button and bottom bar).
  const handleRequestSignOff = useCallback(() => {
    if (!onSignOff || isSigningOff || isSignedOff) return;
    setShowSignOffConfirm(true);
  }, [onSignOff, isSigningOff, isSignedOff]);

  // FT-402 / FT-403: sign-off button handler — fires AFTER the confirmation dialog
  // confirms. Parent (MaintenanceHistoryView) persists via signOffCompletion and
  // refreshes the selected completion so the banner/badge flip without a remount.
  const handleSignOff = async () => {
    setShowSignOffConfirm(false);
    if (!onSignOff || isSigningOff) return;
    setIsSigningOff(true);
    try {
      await onSignOff();
    } finally {
      setIsSigningOff(false);
    }
  };

  // FT-403: wire Sign Off into the mobile bottom bar (middle slot) so it isn't
  // crammed into the header on small screens. The header button stays for desktop
  // (lg:hidden on the bar means this slot is mobile-only).
  useSetBottomBarActions(
    canSignOff && onSignOff && !isSignedOff
      ? [
          {
            key: 'maintenance-sign-off',
            icon: ShieldCheck,
            label: isSigningOff ? 'Signing…' : 'Sign Off',
            primary: true,
            disabled: isSigningOff,
            onClick: handleRequestSignOff,
          },
        ]
      : []
  );

  if (!completion) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Completion record not found</p>
      </div>
    );
  }

  // Determine outcome/status
  const isPassed = completion.outcome === 'pass' || completion.defectsFound === false;
  const OutcomeIcon = isPassed ? CheckCircle : XCircle;
  const outcomeColor = isPassed ? 'green' : 'red';
  const outcomeLabel = isPassed ? 'Passed' : 'Failed';

  // FT-267 §15: prefer write-time-resolved names on the record; fall back to
  // live lookup filtered by completion.equipmentIds for legacy records.
  // The previous implementation mapped the full vessel equipment register —
  // see commit c01d526e (2026-05-19) for the inverted ternary that caused
  // "Equipment Checked (17)" instead of the linked subset.
  const linkedEquipmentNames = completion.equipmentNames?.length > 0
    ? completion.equipmentNames
    : (completion.equipmentIds || [])
        .map(id => equipment.find(e => e.id === id))
        .filter(Boolean)
        .map(e => e.name || e.item || e.label || 'Unknown Equipment');

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-gray-50"
    >
      <SwipeIndicator progress={swipeProgress} />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Desktop-only Back button. Mobile uses the bottom bar + swipe (FT-357). */}
              {onBack && (
                <button
                  onClick={onBack}
                  title="Back"
                  aria-label="Back"
                  className="hidden lg:inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </button>
              )}
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Maintenance Completion</h1>
                <p className="text-sm text-gray-500">View completion details and audit trail</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {onEdit && !isSignedOff && (
                <button
                  onClick={handleEditClick}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                >
                  <Edit3 className="h-4 w-4" />
                  Edit
                </button>
              )}
              {/* FT-402 / FT-403: Sign Off button — skipper+ only, desktop only.
                  On mobile this button is in the bottom bar (useSetBottomBarActions above).
                  `hidden lg:inline-flex` keeps it off mobile where the bottom bar shows it. */}
              {!isSignedOff && canSignOff && onSignOff && (
                <button
                  onClick={handleRequestSignOff}
                  disabled={isSigningOff}
                  data-testid="maintenance-history-sign-off-button"
                  className="hidden lg:inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSigningOff ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="h-4 w-4" />
                  )}
                  {isSigningOff ? 'Signing...' : 'Sign Off'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto p-6 space-y-6">

        {/* FT-402: sign-off status indicators (mirrors DrillDetailView).
            Maintenance fields: signedOffBy / signedOffRole / signedOffAt. */}
        {isSignedOff && (
          <div
            className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg"
            data-testid="maintenance-history-sign-off-banner-signed"
          >
            <Lock className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-green-700">
              Maintenance signed off by {formatUserIdentity(completion.signedOffBy, completion.signedOffRole)} on {formatTimestamp(completion.signedOffAt)}
            </span>
          </div>
        )}
        {isAwaitingSignOff && (
          <div
            className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg"
            data-testid="maintenance-history-sign-off-banner-pending"
          >
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">
              Awaiting sign-off — a skipper or admin needs to review and sign off this maintenance record.
            </span>
          </div>
        )}

        {/* Section 1: Completion Details */}
        <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <div className="bg-slate-700 px-5 py-3">
            <h2 className="text-base font-semibold text-white">Completion Details</h2>
          </div>
          <div className="p-6">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Job Name (if available) */}
              {(job?.item || completion.jobName) && (
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Maintenance Job</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-medium flex items-center gap-1">
                    <Wrench className="h-4 w-4 text-gray-400" />
                    {job?.item || completion.jobName}
                  </dd>
                </div>
              )}

              <div>
                <dt className="text-sm font-medium text-gray-500">Date Completed</dt>
                <dd className="mt-1 text-sm text-gray-900 flex items-center gap-1">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  {formatDate(completion.completedAt || completion.completedDate || completion.date)}
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500">Performed By</dt>
                {/* FT-221: Pass completion for performerName check */}
                <dd className="mt-1 text-sm text-gray-900 flex items-center gap-1">
                  <User className="h-4 w-4 text-gray-400" />
                  {getCrewDisplayName(completion.performedBy || completion.completedBy, completion)}
                </dd>
              </div>

              {completion.duration && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Duration</dt>
                  <dd className="mt-1 text-sm text-gray-900 flex items-center gap-1">
                    <Clock className="h-4 w-4 text-gray-400" />
                    {completion.duration} minutes
                  </dd>
                </div>
              )}

              {/* FT-267 §16: Category — canonical → human label via
                  getCategoryLabel; free-text custom shown verbatim. Hidden
                  for legacy records that never had a category set. */}
              {completion.category && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Category</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {getCategoryLabel(completion.category)}
                  </dd>
                </div>
              )}

              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Outcome</dt>
                <dd className="mt-1">
                  <Badge color={outcomeColor}>
                    <OutcomeIcon className="h-3 w-3 mr-1" />
                    {outcomeLabel}
                  </Badge>
                </dd>
              </div>
            </dl>
          </div>
        </section>

        {/* Section 2: Equipment Checked */}
        {linkedEquipmentNames.length > 0 && (
          <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <Link2 className="h-5 w-5 text-white" />
                Equipment Checked ({linkedEquipmentNames.length})
              </h2>
            </div>
            <div className="p-6">
              <ul className="divide-y divide-gray-100">
                {linkedEquipmentNames.map((name, index) => (
                  <li key={index} className="py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                      <Settings className="h-4 w-4 text-cyan-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-900">{name}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Section 3: Checklist Results */}
        {completion.checklist && completion.checklist.length > 0 && (
          <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-white" />
                Checklist
              </h2>
            </div>
            <div className="p-6">
              <ul className="space-y-2">
                {completion.checklist.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    {item.checked ? (
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${item.checked ? 'text-gray-900' : 'text-red-700'}`}>
                      {item.item || item.label || item.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Section 4: Notes */}
        {completion.notes && (
          <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <FileText className="h-5 w-5 text-white" />
                Inspector Notes
              </h2>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{completion.notes}</p>
            </div>
          </section>
        )}

        {/* FT-349e: Evidence Photos — thumbnail grid + click-for-full-size viewer.
            Reuses <PhotoAttachment disabled> so the viewer/capture pipeline stays
            single-sourced. Only rendered when the completion has photos. */}
        {completion.photos?.length > 0 && (
          <section
            ref={photosRef}
            className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden"
            data-testid="completion-photos-section"
          >
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <Camera className="h-5 w-5 text-white" />
                Evidence Photos ({completion.photos.length})
              </h2>
            </div>
            <div className="p-6">
              <PhotoAttachment
                recordType="maintenance_job"
                recordId={completion.id}
                vesselId={currentVesselId}
                capturedBy=""
                photos={completion.photos}
                onChange={() => {}}
                disabled={true}
                caption={true}
                testId="completion-report-photos"
              />
            </div>
          </section>
        )}

        {/* FT-209: Corrective Job Origin - show if job was created from a defect */}
        {job?.originatingDefectId && (
          <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden border-l-[3px] border-l-orange-500">
            <div className="bg-orange-50 px-5 py-3">
              <h2 className="text-base font-semibold text-orange-800 flex items-center gap-2">
                <Wrench className="h-4 w-4" />
                Corrective Job
              </h2>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <Badge color="orange">Corrective</Badge>
                <span className="text-sm text-gray-600">This job was created to resolve a defect</span>
              </div>
              <button
                onClick={() => onNavigateToDefect?.(job.originatingDefectId)}
                className="text-cyan-600 hover:text-cyan-700 font-medium flex items-center gap-1 hover:underline text-sm"
              >
                <Link2 className="h-4 w-4" />
                View Originating Defect: {job.originatingDefectId}
              </button>
            </div>
          </section>
        )}

        {/* Section 5: Defects Found (during this maintenance check) */}
        {(completion.defectsFound || completion.linkedDefectId) && (
          <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden border-l-[3px] border-l-red-500">
            <div className="bg-red-50 px-5 py-3">
              <h2 className="text-base font-semibold text-red-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Defects Found During Check
              </h2>
            </div>
            <div className="p-6">
              {completion.defectDescription ? (
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{completion.defectDescription}</p>
              ) : (
                <p className="text-sm text-gray-700">
                  Defects were identified during this maintenance check.
                </p>
              )}
              {completion.linkedDefectId && (
                <div className="mt-3 pt-3 border-t border-red-100">
                  <button
                    onClick={() => onNavigateToDefect?.(completion.linkedDefectId)}
                    className="text-cyan-600 hover:text-cyan-700 font-medium flex items-center gap-1 hover:underline text-sm"
                  >
                    <Link2 className="h-4 w-4" />
                    View Linked Defect: {completion.linkedDefectId}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Section 6: Audit Trail */}
        <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <button
            onClick={() => setAuditExpanded(!auditExpanded)}
            className="w-full bg-slate-700 px-5 py-3 flex items-center justify-between hover:bg-slate-500 transition-colors"
          >
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <History className="h-5 w-5 text-white" />
              Audit Trail {completion.auditLog && completion.auditLog.length > 0 && `(${completion.auditLog.length} ${completion.auditLog.length === 1 ? 'entry' : 'entries'})`}
            </h2>
            {auditExpanded ? (
              <ChevronUp className="h-5 w-5 text-white" />
            ) : (
              <ChevronDown className="h-5 w-5 text-white" />
            )}
          </button>
          {auditExpanded && (
            <div className="px-6 py-4 space-y-3 text-sm max-h-96 overflow-y-auto">
              {completion.auditLog && completion.auditLog.length > 0 ? (
                completion.auditLog.slice().reverse().map((entry, index) => (
                  <div key={entry.id || index} className="text-gray-700 pb-3 border-b border-gray-100 last:border-0 last:pb-0">
                    <div className="font-medium flex items-center gap-2">
                      <span className="text-cyan-600">•</span>
                      <span>
                        {entry.action === 'created' ? 'Created' : 'Edited'} by {entry.user}
                      </span>
                      <span className="text-gray-400">-</span>
                      <span className="text-gray-500 text-xs">{formatTimestamp(entry.timestamp)}</span>
                    </div>
                    <div className="ml-4 text-gray-600 space-y-1 mt-1">
                      {/* Summary */}
                      {entry.summary && entry.action !== 'created' && (
                        <div className="text-sm">{entry.summary}</div>
                      )}

                      {/* Field-level changes */}
                      {entry.changes && entry.changes.length > 0 && (
                        <div className="ml-2 text-xs space-y-0.5 bg-gray-50 rounded p-2 mt-1">
                          {entry.changes.map((change, changeIndex) => (
                            <div key={changeIndex} className="text-gray-500">
                              <span className="font-medium text-gray-600">{change.field.replace(/_/g, ' ')}:</span>
                              {' "'}
                              <span className="text-red-500 line-through">
                                {String(change.displayOld || change.oldValue || 'empty')}
                              </span>
                              {'" → "'}
                              <span className="text-green-600">
                                {String(change.displayNew || change.newValue)}
                              </span>
                              {'"'}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Reason */}
                      {entry.reason && entry.reason !== 'Initial record creation' && (
                        <div className="text-xs mt-1">
                          <span className="font-medium">Reason:</span> {entry.reason}
                        </div>
                      )}

                      {/* Vessel */}
                      {entry.vessel && (
                        <div className="text-xs text-gray-400">Vessel: {entry.vessel}</div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                // Fallback for completions without audit log (legacy records)
                // FT-221: Use performerName if present
                <div className="text-gray-700">
                  <div className="font-medium flex items-center gap-2">
                    <span className="text-cyan-600">•</span>
                    <span>Completed by {completion.performerName || completion.performedBy || completion.completedBy || 'Unknown user'}</span>
                    <span className="text-gray-400">-</span>
                    <span className="text-gray-500 text-xs">
                      {completion.createdAt ? formatTimestamp(completion.createdAt) :
                       formatTimestamp(completion.completedAt || completion.completedDate || completion.date)}
                    </span>
                  </div>
                  <div className="ml-4 text-gray-600 mt-1">
                    <div className="text-xs">Maintenance check completed (legacy record - created before audit system implemented)</div>
                    {completion.vesselName && (
                      <div className="text-xs text-gray-400 mt-1">Vessel: {completion.vesselName}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Record metadata footer */}
        <div className="text-center text-xs text-gray-400 py-4">
          Record ID: {completion.id}
          {completion.updatedAt && (
            <span className="ml-2">• Last updated: {formatTimestamp(completion.updatedAt)}</span>
          )}
          {completion.vesselName && (
            <span className="ml-2">• Vessel: {completion.vesselName}</span>
          )}
        </div>
      </div>

      {/* FT-403: Sign Off confirmation dialog.
          Rendered as a simple inline modal — reuses the same portal pattern as
          EditReasonModal (fixed inset overlay + centred card) without a text-input
          because no reason capture is needed for sign-off (it is an audit action
          tracked by the signed-off banner, not a "reason for change" edit). */}
      {showSignOffConfirm && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          data-testid="maintenance-signoff-confirm-dialog"
        >
          <div
            className="fixed inset-0 bg-black bg-opacity-25"
            onClick={() => setShowSignOffConfirm(false)}
          />
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-green-50">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Sign off this record?
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSignOffConfirm(false)}
                  className="text-gray-400 hover:text-gray-500"
                  aria-label="Close"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              {/* Body */}
              <div className="p-6">
                <p className="text-sm text-gray-700">
                  By confirming, you are signing off as a skipper or admin that you have reviewed
                  this entry. This action cannot be undone.
                </p>
              </div>
              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowSignOffConfirm(false)}
                  data-testid="maintenance-signoff-cancel-btn"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSignOff}
                  data-testid="maintenance-signoff-confirm-btn"
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 flex items-center gap-2"
                >
                  <ShieldCheck className="h-4 w-4" />
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Reason Modal */}
      <EditReasonModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onConfirm={handleEditConfirm}
        item={completion}
        recordType="maintenance completion"
      />
    </div>
  );
};

export default MaintenanceCompletionReport;
